import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Image as ImageIcon, RotateCcw, X } from 'lucide-react';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { useTranslation } from '../../../i18n';
import useSyncedState from './useSyncedState';
import ReferenceScale from './ReferenceScale';

// Знайди відмінності — two pictures side by side. The teacher marks each
// difference as a circle (x, y, r in % of the picture width/height) on the
// config panel; the student clicks either picture and every hit circles the
// spot on both. Synced state: { found: [differenceId], rev }.

const createInitialState = () => ({ found: [] });

// Largest box with the given aspect ratio that fits inside the container.
const useFitBox = (aspect) => {
  const ref = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const cw = el.clientWidth;
      const ch = el.clientHeight;
      if (!cw || !ch) return;
      const w = Math.min(cw, ch * aspect);
      setBox({ w, h: w / aspect });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [aspect]);
  return [ref, box];
};

export const hitDifference = (differences, found, x, y, aspect) => {
  // Distances in "% of width" units so circles stay round on any aspect.
  return differences.find((d) => {
    if (found.includes(d.id)) return false;
    const dx = x - d.x;
    const dy = (y - d.y) / aspect;
    return Math.sqrt(dx * dx + dy * dy) <= (d.r || 6);
  }) || null;
};

const Picture = ({ src, aspect, differences, found, showAll, markerColor, onPick, misses, onLoadAspect }) => {
  const [ref, box] = useFitBox(aspect);
  const handleClick = (e) => {
    if (!onPick) return;
    const rect = e.currentTarget.getBoundingClientRect();
    onPick(((e.clientX - rect.left) / rect.width) * 100, ((e.clientY - rect.top) / rect.height) * 100);
  };
  return (
    <div ref={ref} className="flex-1 min-w-0 min-h-0 flex items-center justify-center">
      {src ? (
        <div
          className="relative overflow-hidden rounded-xl bg-white"
          style={{ width: box.w, height: box.h, cursor: onPick ? 'crosshair' : 'default' }}
          onClick={handleClick}
        >
          <img
            src={resolveMediaUrl(src) || ''}
            alt=""
            draggable={false}
            onLoad={(e) => onLoadAspect?.(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight)}
            className="absolute inset-0 w-full h-full select-none"
            style={{ objectFit: 'fill' }}
          />
          {differences.map((d) => {
            const isFound = found.includes(d.id);
            if (!isFound && !showAll) return null;
            const size = `${(d.r || 6) * 2}%`;
            return (
              <div
                key={d.id}
                className="absolute rounded-full pointer-events-none"
                style={{
                  left: `${d.x}%`,
                  top: `${d.y}%`,
                  width: size,
                  aspectRatio: '1 / 1',
                  transform: 'translate(-50%, -50%)',
                  border: isFound ? `3px solid ${markerColor}` : `2px dashed ${markerColor}`,
                  opacity: isFound ? 1 : 0.55,
                  boxShadow: isFound ? '0 0 0 2px rgba(255,255,255,0.8)' : 'none',
                }}
              />
            );
          })}
          {misses.map((m) => (
            <div
              key={m.id}
              className="absolute pointer-events-none text-red-500 animate-ping"
              style={{ left: `${m.x}%`, top: `${m.y}%`, transform: 'translate(-50%, -50%)', animationIterationCount: 1 }}
            >
              <X size={26} strokeWidth={3} />
            </div>
          ))}
        </div>
      ) : (
        <div className="w-full h-full rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-300">
          <ImageIcon size={40} />
        </div>
      )}
    </div>
  );
};

const SpotDifferenceInner = ({
  element,
  isEditor = false,
  canInteract = true,
  showAnswers = false,
  syncedState = null,
  onStateChange,
}) => {
  const { t } = useTranslation();
  const {
    title = '',
    imageA = null,
    imageB = null,
    differences = [],
    layout = 'horizontal',
    markerColor = '#2A4D25',
    textColor = '#1f2937',
    fontFamily = 'Evolventa',
    fontSize = 18,
  } = element.content || {};

  const [state, update] = useSyncedState(syncedState, onStateChange, createInitialState);
  const [aspect, setAspect] = useState(element.content?.imageAspect || 4 / 3);
  const [misses, setMisses] = useState([]);
  const missTimers = useRef([]);
  useEffect(() => () => missTimers.current.forEach(clearTimeout), []);

  const validIds = new Set(differences.map((d) => d.id));
  const found = (state.found || []).filter((id) => validIds.has(id));
  const total = differences.length;
  const complete = total > 0 && found.length >= total;
  const interactive = !isEditor && canInteract;

  const pick = (x, y) => {
    if (!interactive || complete) return;
    const hit = hitDifference(differences, found, x, y, aspect);
    if (hit) {
      update((prev) => ({ ...prev, found: [...(prev.found || []).filter((id) => validIds.has(id)), hit.id] }));
      return;
    }
    const miss = { id: `${Date.now()}_${Math.random()}`, x, y };
    setMisses((m) => [...m, miss]);
    missTimers.current.push(setTimeout(() => setMisses((m) => m.filter((it) => it.id !== miss.id)), 700));
  };

  const pictureProps = {
    aspect,
    differences,
    found,
    showAll: isEditor || showAnswers,
    markerColor,
    misses,
    onPick: interactive ? pick : null,
  };

  return (
    <div className="w-full h-full flex flex-col overflow-hidden" style={{ fontFamily, color: textColor, padding: 8, gap: 8 }}>
      {(title || total > 0) && (
        <div className="shrink-0 flex items-center justify-center gap-3">
          {title && <div className="font-bold leading-tight text-center" style={{ fontSize: fontSize * 1.2 }}>{title}</div>}
          {total > 0 && (
            <span
              className="rounded-full font-semibold tabular-nums"
              style={{
                fontSize: fontSize * 0.85,
                padding: '2px 10px',
                backgroundColor: complete ? markerColor : 'rgba(0,0,0,0.06)',
                color: complete ? '#ffffff' : textColor,
              }}
            >
              {found.length} / {total}
            </span>
          )}
          {interactive && found.length > 0 && (
            <button
              type="button"
              onClick={() => update(() => createInitialState())}
              title={t('games.spotDifference.reset', { defaultValue: 'Почати спочатку' })}
              className="p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100"
            >
              <RotateCcw size={14} />
            </button>
          )}
        </div>
      )}
      <div className={`flex-1 min-h-0 flex ${layout === 'vertical' ? 'flex-col' : 'flex-row'}`} style={{ gap: 10 }}>
        <Picture {...pictureProps} src={imageA} onLoadAspect={(a) => { if (a && Number.isFinite(a)) setAspect(a); }} />
        <Picture {...pictureProps} src={imageB} />
      </div>
    </div>
  );
};

const SpotDifferenceElement = ({ slideScale = 1, ...props }) => (
  <ReferenceScale scale={slideScale}>
    <SpotDifferenceInner {...props} />
  </ReferenceScale>
);

export default SpotDifferenceElement;
